import {
  SERVICE_WORKER_PATH,
  SERVICE_WORKER_SCOPE,
  SKIP_WAITING_MESSAGE,
} from "./pwa-constant";
import {
  ServiceWorkerSubscription,
  ServiceWorkerUpdateHandlers,
} from "./pwa-types";

export const isServiceWorkerSupported = (): boolean =>
  typeof navigator !== "undefined" && "serviceWorker" in navigator;

const watchInstalling = (
  registration: ServiceWorkerRegistration,
  onUpdateReady: ServiceWorkerUpdateHandlers["onUpdateReady"]
): void => {
  const installing = registration.installing;
  if (!installing) return;

  installing.addEventListener("statechange", () => {
    // A controller already exists, so "installed" means an update is waiting,
    // not a first install.
    if (installing.state === "installed" && navigator.serviceWorker.controller)
      onUpdateReady(installing);
  });
};

/**
 * Registers the app-shell worker and reports when a newer build is waiting.
 * Never throws: a failed registration only means no offline shell.
 */
export const subscribeToServiceWorker = async ({
  onUpdateReady,
}: ServiceWorkerUpdateHandlers): Promise<ServiceWorkerSubscription | null> => {
  if (!isServiceWorkerSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.register(
      SERVICE_WORKER_PATH,
      { scope: SERVICE_WORKER_SCOPE }
    );

    if (registration.waiting && navigator.serviceWorker.controller)
      onUpdateReady(registration.waiting);

    const handleUpdateFound = () =>
      watchInstalling(registration, onUpdateReady);
    registration.addEventListener("updatefound", handleUpdateFound);

    return {
      checkForUpdate: async () => {
        try {
          await registration.update();
        } catch {
          // Offline or the server is unreachable; the next check will retry.
        }
      },
      unsubscribe: () =>
        registration.removeEventListener("updatefound", handleUpdateFound),
    };
  } catch (error) {
    console.error("Service worker registration failed", error);
    return null;
  }
};

/** Activates the waiting build and reloads once it controls the page. */
export const activateWaitingWorker = (waiting: ServiceWorker): void => {
  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => window.location.reload(),
    { once: true }
  );
  waiting.postMessage({ type: SKIP_WAITING_MESSAGE });
};
